// =============================================================================
// PERMISSIONS.JS — Single source of truth for role-based access.
// =============================================================================
// Every component should ask these functions instead of inlining checks like
// `currentUser.role === 'admin'`. Adding a new permission rule? Edit here.
// =============================================================================

const isAdmin = (u) => u?.role === 'admin'
const isSales = (u) => u?.role === 'sales'
const isAgent = (u) => u?.role === 'agent'
const isCreator = (u) => u?.role === 'creator'

// --- Role checks -------------------------------------------------------------
export const Roles = { isAdmin, isSales, isAgent, isCreator }

// --- Tab visibility ----------------------------------------------------------
// `tabId` is the tab key registered in App.js TABS array.
export function canSeeTab(user, tabId, opts = {}) {
  if (isAdmin(user)) return true
  if (isSales(user))   return ['deals', 'crm', 'reports', 'firstlook', 'todo'].includes(tabId)
  if (isCreator(user)) return ['dashboard', 'deals', 'creators'].includes(tabId)
  if (isAgent(user))   return !opts.adminOnly
  return false
}

// --- Deal-level permissions --------------------------------------------------
// canEditDeal: who can update deal fields & status.
//
// `managedCreators` is the Set of creator names this agent runs today (derived
// from creators.default_agent — see `managedCreators` in App.js). An agent who
// takes an account over owns it going forward, which includes correcting the
// back catalogue they inherited. Without this, someone handed an existing
// roster can see that history but not touch a single row of it.
//
// This is scoped to their OWN creators, so it does not become a general edit
// grant over other agents' work. Note it does not reassign anything: the
// commission split is keyed off each deal's `agent` column, so editing an
// inherited deal leaves the original agent credited unless that field is
// deliberately changed.
export function canEditDeal(user, deal, agentName, managedCreators) {
  if (isAdmin(user) || isSales(user)) return true
  if (deal?.created_by === user?.id) return true
  if (deal?.agent && agentName && deal.agent === agentName) return true
  if (deal?.creator && managedCreators?.has?.(deal.creator)) return true
  return false
}

// canDeleteDeal: stricter than edit.
export function canDeleteDeal(user) {
  return isAdmin(user)
}

// canExportDeals: only admin (compensation/financials visible).
export function canExportDeals(user) {
  return isAdmin(user)
}

// canParseContract: anyone who books deals. Creators are excluded — they don't
// enter deals for the agency. Enforced again server-side in the
// parse-contract edge function.
export function canParseContract(user) {
  return isAdmin(user) || isAgent(user) || isSales(user)
}

// canSeeCommission: anyone non-creator can see commission column.
// Admin always sees totals; agents see only their own (handled in component).
export function canSeeCommissionColumn(user) {
  return !isCreator(user)
}

// --- Reports tabs ------------------------------------------------------------
// Commission Payouts contains pay info — admin only.
export function canSeeCommissionPayouts(user) {
  return isAdmin(user)
}

// --- CRM ---------------------------------------------------------------------
// Admin + sales see all team members' CRM tabs.
export function canSeeAllCrmTabs(user) {
  return isAdmin(user) || isSales(user)
}

// --- Settings ----------------------------------------------------------------
export function canManageTeam(user) {
  return isAdmin(user)
}

// --- View mode ---------------------------------------------------------------
// Sales acts like admin view (agency-wide totals) without admin privileges.
export function getEffectiveViewMode(user, currentViewId) {
  if (isAdmin(user) && currentViewId === 'admin') return 'admin'
  if (isSales(user)) return 'admin'
  return 'agent'
}
